import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Save } from "lucide-react";

/**
 * BookingFieldsSection — dedicated Rental-Settings block for the 3-status
 * checkout / manual-booking form field configuration.
 *
 * Owns its own settings copy + save button so a mis-click on the huge
 * Business Settings save button can never revert the toggles. After a
 * successful PUT we set local state from the backend's response envelope
 * — which fixes the "toggle briefly flashes back to OFF" bug reported
 * by the user.
 */
const FIELD_GROUPS = [
  {
    label: "Customer identity",
    fields: [
      ["full_name", "Full name"],
      ["email",     "Email"],
      ["phone",     "Phone"],
      ["address",   "Address"],
      ["country",   "Country"],
      ["city",      "City"],
      ["zip",       "Zip / Postal code"],
      ["notes",     "Notes"],
    ],
  },
  {
    label: "Driver identity",
    hint:  "Applied to both public checkout and admin manual bookings.",
    fields: [
      ["id_passport",    "ID / Passport number"],
      ["license_number", "Driver's licence number"],
      ["driver_age",     "Driver's age"],
    ],
  },
  {
    label: "Airport pickup / return",
    hint:  "Shown when a location's type = Airport.",
    fields: [
      ["airline_company", "Airline / carrier"],
      ["flight_number",   "Flight number"],
      ["flight_time",     "Flight time"],
    ],
  },
  {
    label: "Port / cruise pickup",
    hint:  "Shown when a location's type = Port.",
    fields: [
      ["vessel_company",      "Cruise line / operator"],
      ["vessel_name",         "Vessel name"],
      ["vessel_arrival_time", "Arrival time"],
    ],
  },
  {
    label: "Accommodation delivery",
    hint:  "Shown when a location's type = Hotel / Villa / Apartment.",
    fields: [
      ["accommodation_name",    "Hotel / villa / apartment name"],
      ["accommodation_address", "Accommodation address"],
    ],
  },
];

const STATUS_STYLES = {
  off: {
    active: "bg-neutral-700 text-white dark:bg-neutral-200 dark:text-neutral-900",
    idle:   "text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800",
  },
  optional: {
    active: "bg-amber-600 text-white",
    idle:   "text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20",
  },
  required: {
    active: "bg-emerald-700 text-white",
    idle:   "text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20",
  },
};

export default function BookingFieldsSection() {
  const [fields, setFields] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        setFields({ ...(data.checkout_fields || {}) });
      } catch (err) {
        toast.error("Failed to load settings", {
          description: formatApiError(err?.response?.data?.detail) || err.message,
        });
      }
    })();
  }, []);

  const setStatus = (key, status) => {
    setFields((prev) => ({ ...(prev || {}), [key]: status }));
    setDirty(true);
  };

  const save = async () => {
    if (!fields) return;
    setSaving(true);
    try {
      const { data: current } = await api.get("/admin/settings");
      const { data } = await api.put("/admin/settings", {
        ...current,
        checkout_fields: fields,
      });
      // Trust the backend echo — kills any "flash back to OFF" glitch.
      setFields({ ...(data.checkout_fields || {}) });
      setDirty(false);
      toast.success("Booking fields saved");
    } catch (err) {
      toast.error("Save failed", {
        description: formatApiError(err?.response?.data?.detail) || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!fields) {
    return <p className="text-sm text-neutral-500">Loading booking fields…</p>;
  }

  return (
    <div className="space-y-6" data-testid="booking-fields-section">
      <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
        Configure every field on the public Checkout form <strong>and</strong> the
        Admin manual booking form. Set OFF (hidden + skipped in validation),
        OPTIONAL (rendered but not required), or REQUIRED (rendered and mandatory
        with a red asterisk). Airport / Port / Accommodation groups only appear
        when the pickup or return location is classified as that type.
      </p>

      {FIELD_GROUPS.map((group) => (
        <div key={group.label}>
          <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400">
            {group.label}
          </p>
          {group.hint && (
            <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1 italic">
              {group.hint}
            </p>
          )}
          <div className="mt-3 space-y-2">
            {group.fields.map(([key, label]) => {
              const current = fields[key] || "off";
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3"
                  data-testid={`cf-row-${key}`}
                >
                  <span className="text-sm">
                    {label}
                    {current === "required" && (
                      <span className="text-red-600 ml-1" aria-hidden="true">*</span>
                    )}
                  </span>
                  <div className="inline-flex border border-black/15 dark:border-white/15 rounded overflow-hidden">
                    {["off", "optional", "required"].map((status) => {
                      const isActive = current === status;
                      const style = STATUS_STYLES[status];
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setStatus(key, status)}
                          className={`px-3 py-1 text-[11px] uppercase tracking-wider transition-colors ${isActive ? style.active : style.idle}`}
                          data-testid={`cf-${key}-${status}`}
                          aria-pressed={isActive}
                        >
                          {status}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="pt-2 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 bg-black text-white dark:bg-white dark:text-black px-6 py-2.5 text-sm rounded disabled:opacity-40"
          data-testid="cf-save"
        >
          <Save size={14} />
          {saving ? "Saving…" : dirty ? "Save booking fields" : "Saved"}
        </button>
      </div>
    </div>
  );
}
